import clsx from 'clsx';
import { doubleDaySeverity } from '@/domain/workload';

interface Props {
  count: number;
  className?: string;
  /** Bigger treatment + a pulse on the critical (red) state, for prominent placements. */
  prominent?: boolean;
  /** Optional artist-name prefix, for placements away from the artist's name. */
  name?: string;
}

/**
 * "N double days since last day off" — hidden at 0, amber at 1, red at 2+.
 * Lets a manager decide when to grant a comp day. `prominent` renders it larger
 * and (on the red/critical state) gently pulsing — used in the calendar filter row.
 */
export default function DoubleDayBadge({ count, className, prominent = false, name }: Props) {
  const severity = doubleDaySeverity(count);
  if (severity === 'none') return null;
  const critical = severity === 'critical';
  const label = `${name ? `${name} · ` : ''}${count} double day${count === 1 ? '' : 's'} since last day off`;
  return (
    <span
      data-testid="double-day-badge"
      title="Double days worked since this artist's last day off"
      className={clsx(
        'inline-flex items-center rounded-full border font-semibold uppercase tracking-wide',
        prominent ? 'gap-2 px-3.5 py-1.5 text-xs' : 'gap-1 px-2 py-0.5 text-[10px]',
        critical
          ? 'bg-blood/15 text-blood border-blood/60'
          : 'bg-gold/15 text-gold-bright border-gold-bright/50',
        prominent && critical && 'motion-safe:animate-ring-pulse-blood',
        className,
      )}
    >
      {prominent ? <span className="h-2 w-2 rounded-full bg-current" aria-hidden /> : null}
      {label}
    </span>
  );
}
